import type { ReactNode } from "react";

/**
 * Centered status screen shared by the 404 / 500 routes (public + office)
 * and demoed in `/design`. Presentation only: the caller supplies the
 * eyebrow already formatted (public passes `toEyebrow(...)`, office passes
 * plain text) and the action controls (FramedLink/CtaButton on the public
 * site, ActionLink in the office), so this component stays free of i18n and
 * routing concerns. Built from /design tokens, so it adapts to the office's
 * forced-dark theme automatically.
 */
export function StatusScreen({
  code,
  eyebrow,
  title,
  body,
  actions,
}: {
  /** Big decorative status number, e.g. "404" / "500". */
  code: string;
  /** Small mono label above the title (pre-formatted by the caller). */
  eyebrow?: ReactNode;
  title: string;
  body?: string;
  /** One or more links/buttons. */
  actions?: ReactNode;
}) {
  return (
    <section className="mx-auto flex min-h-[60vh] max-w-site flex-col items-center justify-center px-4 py-24 text-center sm:px-8">
      <p aria-hidden className="display text-7xl text-fg-subtle sm:text-9xl">
        {code}
      </p>
      {eyebrow && <p className="label mt-10">{eyebrow}</p>}
      <h1 className="display mt-4 max-w-2xl text-3xl text-fg sm:text-5xl">
        {title}
      </h1>
      {body && (
        <p className="mt-6 max-w-xl text-base text-fg-muted sm:text-lg">
          {body}
        </p>
      )}
      {actions && (
        <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
          {actions}
        </div>
      )}
    </section>
  );
}
